import { isAfter } from 'date-fns';
import InboxBadge from '../components/app-features/inbox/InboxBadge';
import UpdatesWarning from '../components/app-features/profile/sub-pages/my-achievements/badges/UpdatesWarning';
import VerificationEmploymentDetailsBadge from '../components/app-features/profile/sub-pages/verification/badges/VerificationEmploymentDetailsBadge';
import VerificationNotificationBadge from '../components/app-features/profile/sub-pages/verification/badges/VerificationNotificationBadge';
import VerificationRequestsCountBadge from '../components/app-features/profile/sub-pages/verification/badges/VerificationRequestsCountBadge';
import { Layout } from '../components/handler-components/ResizeHandler';
import LazyViewMoreButton from '../components/lazy-loaded/buttons/LazyViewMoreButton';
import LazyMyAchievements from '../components/lazy-loaded/my-promotions/LazyMyAchievements';
import LazyVerificationSaferGamblingQuestionsBadge from '../components/lazy-loaded/profile/verification/badges/LazyVerificationSaferGamblingQuestionsBadge';
import { auth } from '../data-types/initialisation/initialisationConstants';
import { MY_ACHIEVEMENTS_TAB_ID, PROMOTIONS_INBOX } from '../data-types/profile/profile-menu/profileMenuConstants';
import { ProfileMenuObject } from '../data-types/profile/profile-menu/profileMenuInterfaces';
import { PendingQuestion } from '../data-types/user/userInterfaces';
import { PATHS } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { setDesktopProfilePath } from '../state/actions/navigationActions';
import { store } from '../state/store';

export const tryToDisplayNotificationWarningBadge = (menuItem: ProfileMenuObject) => {
  if (menuItem.canHaveNotificationWarning) {
    switch (menuItem.id) {
      case PATHS.APP.PROFILE.VERIFICATION.ROOT:
        return <VerificationNotificationBadge />;
      case MY_ACHIEVEMENTS_TAB_ID:
        return <UpdatesWarning />;
      default:
        return null;
    }
  }

  return null;
};

export const openProfilePathForLoggedInUsers = (profilePath: string, layout: Layout) => {
  if (layout === Layout.DESKTOP) {
    setDesktopProfilePath(profilePath);
  } else {
    browserHistory.push(profilePath);
  }
};

export const tryToDisplayNotificationBadge = (menuItem: ProfileMenuObject) => {
  if (menuItem.canHaveNotificationBadge) {
    switch (menuItem.id) {
      case PROMOTIONS_INBOX:
        return <InboxBadge />;
      case PATHS.APP.PROFILE.VERIFICATION.DOCUMENTS:
        return <VerificationRequestsCountBadge />;
      case PATHS.APP.PROFILE.VERIFICATION.EMPLOYMENT_DETAILS.ROOT:
        return <VerificationEmploymentDetailsBadge />;
      case PATHS.APP.PROFILE.VERIFICATION.SAFER_GAMBLING.ROOT:
        return <LazyVerificationSaferGamblingQuestionsBadge />;
      default:
        return null;
    }
  }

  return null;
};

export const openProfileStayingInControl = () => {
  const {
    userData: { authentication },
    navigation: { layout },
  } = store.getState();
  const isDesktop = layout === Layout.DESKTOP;
  const isAuthenticated = authentication === auth.AUTHENTICATED;
  const newPath = PATHS.APP.PROFILE.CONTROL;

  if (isAuthenticated) {
    openProfilePathForLoggedInUsers(newPath, layout);
  } else {
    if (isDesktop) {
      setDesktopProfilePath(newPath);
    }

    browserHistory.push(PATHS.LOGIN.ROOT, { redirectedFrom: newPath });
  }
};

export const computeMyAchievementsMenuProps = (
  layout: Layout,
  anyChanges: boolean,
  hasSeenMyAchievementsBefore: boolean,
  alternativeRedirectHandler?: (url: string) => void,
): ProfileMenuObject => {
  const isMobileView = layout === Layout.MOBILE;

  const viewMoreCallback = () => {
    const redirect = alternativeRedirectHandler || browserHistory.push;
    redirect(MY_ACHIEVEMENTS_TAB_ID);
  };

  const displayAchievementsInDropdownInsteadOfNewPage = isMobileView || !hasSeenMyAchievementsBefore;
  const shouldDisplayQuickPreviewInDesktop = !isMobileView && !hasSeenMyAchievementsBefore;

  return {
    id: MY_ACHIEVEMENTS_TAB_ID,
    dataTestId: 'profile-menu-my-achievements',
    titleKey: 'profile.my_achievements',
    collapse: isMobileView,
    canHaveNotificationWarning: anyChanges,
    customElementInsteadOfDropdownMenu:
      shouldDisplayQuickPreviewInDesktop || displayAchievementsInDropdownInsteadOfNewPage ? (
        <LazyMyAchievements props={{ hasSeenMyAchievementsBefore, fastPreview: shouldDisplayQuickPreviewInDesktop }} />
      ) : undefined,
    customElementInsteadOfArrow:
      !isMobileView && !hasSeenMyAchievementsBefore ? (
        <LazyViewMoreButton props={{ onClick: viewMoreCallback, customDataTestId: 'my-achievements-view-more-button' }} />
      ) : undefined,
  };
};

export const orderQuestionsByRequestedAtDesc = (latestQuestionsAndAnswers: PendingQuestion[]) =>
  latestQuestionsAndAnswers.sort((itemA, itemB) => {
    if (isAfter(new Date(itemA.requestedAt), new Date(itemB.requestedAt))) {
      return -1;
    }

    if (isAfter(new Date(itemB.requestedAt), new Date(itemA.requestedAt))) {
      return 1;
    }

    return 0;
  });
